import { useChannel } from 'ably/react';
import { useRef, useCallback } from 'react';
import { CommentType, FeedNames, PostType } from '../memeverse_types';
import { useAuth } from '../../../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import {
  addNewCommentInQueryData,
  addNewPostToQueryData,
  deleteCommentInQueryData,
  deletePostFromQueryData,
  editLikeCommentInQueryData,
  editLikePostInQueryData,
  editThinkingCommentInQueryData,
  updateCommentInQueryData,
  updatePostInQueryData
} from '../helper';

export const useAskWebsockets = (
  queryKey: FeedNames,
  jumpToFunc: (postId: string) => void = null,
  onDocumentUpdate?: (documentId: string, type: 'post' | 'comment') => void
) => {
    const newPostCount = useRef<number>(0);
    const { authUser } = useAuth();
    const queryClient = useQueryClient();

    const messageHandler = useCallback((message) => {
        let hasMutatedData = false;
        try {
          if (message.name.startsWith('post.new.')) {
            const docId = message.name.split('.')[2];
            // new post
            let newPost: PostType = message.data;
            newPost.updatedAt ||= newPost.createdAt;
            newPost.docId ||= docId;
            newPost.comments_list ||= [];
            newPost.comments_counts ||= 0;
            newPost.flat_comments_list ||= [];

            if (message.data.images &&
              message.data.images.length &&
              typeof message.data.images[0] !== 'string') {
              try {
                newPost.images = message.data.images.map((imgData: any) => imgData?.image_url || null);
              } catch (e) {

              }
            }

            hasMutatedData = addNewPostToQueryData(
              newPost,
              queryClient,
              queryKey
            )

            if (hasMutatedData) {
              // Trigger individual refresh for this post
              onDocumentUpdate?.(docId, 'post');

              // jump to new post if it was created by the user
              if (newPost.user_id === authUser?.uid && !!jumpToFunc) {
                setTimeout(function() {jumpToFunc(newPost.docId)}, 250)
              } else {
                // keep a running count of new posts for snackbar notification
                newPostCount.current += 1;
              }
            }
          } else if (message.name.startsWith('post.update.')) {
            const docId = message.name.split('.')[2];
            let newPost: PostType = message.data;
            newPost.updatedAt = (new Date()).toString();

            hasMutatedData = updatePostInQueryData(
              newPost,
              queryClient,
              queryKey,
            )

            if (hasMutatedData) {
              onDocumentUpdate?.(docId, 'post');
            }
          } else if (message.name.startsWith('post.delete.')) {
            let docId = message.name.split('.')[2];

            hasMutatedData = deletePostFromQueryData(
              docId,
              queryClient,
              queryKey
            )

            if (hasMutatedData) {
              onDocumentUpdate?.(docId, 'post');
            }
          } else if (message.name.startsWith('comment.new.')) {
            const commentId = message.name.split('.')[2];
            let newCommentData: CommentType = message.data;
            newCommentData.flat_comments_list ||= [];

            hasMutatedData = addNewCommentInQueryData(
              newCommentData,
              queryClient,
              queryKey
            )

            if (hasMutatedData) {
              onDocumentUpdate?.(commentId, 'comment');
              // Also update the parent post
              if (newCommentData.parent_post_id) {
                onDocumentUpdate?.(newCommentData.parent_post_id, 'post');
              }
            }
          } else if (message.name.startsWith('comment.update.')) {
            const commentId = message.name.split('.')[2];
            let newComment: CommentType = message.data;
            newComment.updatedAt = (new Date()).toString();

            hasMutatedData = updateCommentInQueryData(
              newComment,
              queryClient,
              queryKey
            )

            if (hasMutatedData) {
              onDocumentUpdate?.(commentId, 'comment');
            }

          } else if (message.name.startsWith('comment.delete.')) {
            const commentId = message.name.split('.')[2];

            hasMutatedData = deleteCommentInQueryData(
              commentId,
              queryClient,
              queryKey
            )

            if (hasMutatedData) {
              onDocumentUpdate?.(commentId, 'comment');
            }
          } else if (message.name.startsWith('like.new.post.') ||
            message.name.startsWith('like.delete.post.')) {
            let docId = message.name.split('.')[3];
            let shouldAdd = message.name.split('.')[1] === 'new';
            let user_id = message.data?.user_id;

            hasMutatedData = editLikePostInQueryData(
              docId,
              shouldAdd,
              queryClient,
              queryKey,
              authUser?.uid && authUser?.uid === user_id,
            )

            if (hasMutatedData) {
              onDocumentUpdate?.(docId, 'post');
            }
          } else if (message.name.startsWith('like.new.comment.') ||
            message.name.startsWith('like.delete.comment.')) {
            let docId = message.name.split('.')[3];
            let shouldAdd = message.name.split('.')[1] === 'new';
            let user_id = message.data?.user_id;

            hasMutatedData = editLikeCommentInQueryData(
              docId,
              shouldAdd,
              queryClient,
              queryKey,
              authUser?.uid && authUser?.uid === user_id,
            )

            if (hasMutatedData) {
              onDocumentUpdate?.(docId, 'comment');
            }
          } else if (message.name.startsWith('comment.thinking.')) {
            if (message.data.userId !== authUser?.uid) {
              // only handle this for other users thinking
              let removing = message.name.split('.')[2] !== 'new';

              hasMutatedData = editThinkingCommentInQueryData(
                message.data.userId,
                message.data,
                removing,
                queryClient,
                queryKey
              )

              if (hasMutatedData) {
                // For thinking comments, we need to update the parent post
                if (message.data.parentId) {
                  onDocumentUpdate?.(message.data.parentId, 'post');
                }
              }
            }
          }

          } catch (err) {
            console.log('Error: ', err)
          }
        }, [authUser?.uid, queryClient, queryKey, jumpToFunc, onDocumentUpdate]);

    useChannel('ask', messageHandler);

    const resetNewPostCount = () => {
      newPostCount.current = 0;
    };

    return { newPostCount, resetNewPostCount };
}
